import type { AxiosError, AxiosInstance } from 'axios';
import { toast } from 'react-toastify';

export const formatApiError = (error: unknown, responseBody?: unknown): string => {
  const failure = error && typeof error === 'object' ? (error as AxiosError) : undefined;
  const response = failure?.response;
  const body = responseBody === undefined ? response?.data : responseBody;
  const endpoint = failure?.config?.url?.split(/[?#]/)[0];
  const details = [
    failure?.message || (typeof error === 'string' ? error : 'API request failed.'),
    endpoint && `Request: ${failure?.config?.method?.toUpperCase() || 'GET'} ${endpoint}`,
    response ? `HTTP: ${response.status} ${response.statusText || ''}`.trim() : 'No response received from the API.',
    failure?.code && `Code: ${failure.code}`,
    response?.headers?.['x-request-id'] && `Request ID: ${response.headers['x-request-id']}`,
  ].filter(Boolean);

  if (body !== undefined && body !== null && body !== '') {
    details.push(`Response:\n${typeof body === 'string' ? body : JSON.stringify(body, null, 2)}`);
  }
  return details.join('\n');
};

// Report failures before individual services can swallow or replace their details.
// Never include Axios config/headers/request bodies: these can contain credentials.
export const registerApiErrorHandling = (client: AxiosInstance) =>
  client.interceptors.response.use(undefined, async (error: unknown) => {
    const failure = error as AxiosError;
    if (failure?.code !== 'ERR_CANCELED') {
      let body = failure?.response?.data;
      if (body instanceof Blob) {
        try {
          body = await body.text();
        } catch {
          body = 'Could not read the API error response.';
        }
      }
      toast.error(
        `API error — please send these details to your administrator.\nTime: ${new Date().toISOString()}\n${formatApiError(error, body)}`,
        {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          style: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: '70vh', overflowY: 'auto' },
        }
      );
    }
    return Promise.reject(error);
  });
